import {
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
  provideZoneChangeDetection,
  APP_INITIALIZER
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { MSAL_INSTANCE, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

import { routes } from './app.routes';
import { APP_CONFIG } from './config/app.config';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { loadingInterceptor } from './shared/interceptors/loading.interceptor';
import { parameterReducer } from './features/admin/store/parameter.reducer';
import { ParameterEffects } from './features/admin/store/parameter.effects';
import { dependencyReducer } from './features/admin/store/dependency.reducer';
import { DependencyEffects } from './features/admin/store/dependency.effects';
import { modalidadSeleccionReducer } from './features/admin/store/modalidad-seleccion.reducer';
import { ModalidadSeleccionEffects } from './features/admin/store/modalidad-seleccion.effects';
import { actuacionContractualReducer } from './features/admin/store/actuacion-contractual.reducer';
import { ActuacionContractualEffects } from './features/admin/store/actuacion-contractual.effects';
import { tipoContratoReducer } from './features/admin/store/tipo-contrato.reducer';
import { TipoContratoEffects } from './features/admin/store/tipo-contrato.effects';
import { perfilReducer } from './features/admin/store/perfil.reducer';
import { PerfilEffects } from './features/admin/store/perfil.effects';
import { contratoReducer } from './features/admin/store/contrato.reducer';
import { ContratoEffects } from './features/admin/store/contrato.effects';
import { rangoSalarialPerfilReducer } from './features/admin/store/rango-salarial-perfil.reducer';
import { RangoSalarialPerfilEffects } from './features/admin/store/rango-salarial-perfil.effects';
import { msalConfig } from './auth-config';
import { GlobalErrorHandlerService } from './shared/services/global-error-handler.service';

// Variable global para mantener la instancia de MSAL
let msalInstanceGlobal: PublicClientApplication | null = null;

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstanceGlobal) {
    msalInstanceGlobal = new PublicClientApplication(msalConfig);
  }
  return msalInstanceGlobal;
}

// Función para inicializar MSAL antes de que arranque la aplicación
export function initializeMSAL(): () => Promise<void> {
  return async () => {
    const msalInstance = MSALInstanceFactory();
    
    try {
      await msalInstance.initialize();
      
      // Intentar cargar cuentas existentes
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    } catch (error) {
      // Error al inicializar MSAL
      throw error;
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Inicializar MSAL ANTES de que arranque la aplicación
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMSAL,
      multi: true
    },
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([errorInterceptor, loadingInterceptor])),
    provideAnimations(),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true
    }),
    provideStore({
      parameter: parameterReducer,
      dependency: dependencyReducer,
      modalidadSeleccion: modalidadSeleccionReducer,
      actuacionContractual: actuacionContractualReducer,
      tipoContrato: tipoContratoReducer,
      perfil: perfilReducer,
      contrato: contratoReducer,
      rangoSalarialPerfil: rangoSalarialPerfilReducer
    }),
    provideEffects([
      ParameterEffects,
      DependencyEffects,
      ModalidadSeleccionEffects,
      ActuacionContractualEffects,
      TipoContratoEffects,
      PerfilEffects,
      ContratoEffects,
      RangoSalarialPerfilEffects
    ]),
    ...(isDevMode()
      ? [
          provideStoreDevtools({
            maxAge: 25,
            logOnly: true,
            autoPause: true,
            trace: false,
            traceLimit: 75
          })
        ]
      : []),
    {
      provide: APP_CONFIG,
      useValue: APP_CONFIG
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ]
};
